import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import config from '../config';
import './AuthConfirm.css';

const AuthConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error | reset-form
  const [message, setMessage] = useState('');
  const [emailType, setEmailType] = useState('');

  // Password reset form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type') || 'signup';
    setEmailType(type);

    if (!tokenHash) {
      setStatus('error');
      setMessage('This confirmation link is missing or has expired. Please try again.');
      return;
    }

    const verify = async () => {
      try {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });

        if (error) {
          if (error.message?.toLowerCase().includes('expired')) {
            setMessage('This confirmation link has expired. Please request a new one.');
          } else if (error.message?.toLowerCase().includes('already')) {
            setMessage('Already confirmed. You can log in below.');
            setStatus('success');
            return;
          } else {
            setMessage('This link is invalid or has already been used.');
          }
          setStatus('error');
          return;
        }

        // Handle each type differently after successful verification
        if (type === 'recovery') {
          // Show password reset form — session is now active
          setStatus('reset-form');
        } else if (type === 'magiclink') {
          // User is now fully logged in — send to dashboard
          navigate('/dashboard');
        } else {
          // signup or email_change — show success
          setStatus('success');
        }
      } catch {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    };

    verify();
  }, [searchParams, navigate]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setResetError(error.message || 'Failed to update password. Please try again.');
      } else {
        await supabase.auth.signOut();
        setStatus('success');
        setEmailType('recovery-done');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const successMessage = () => {
    if (emailType === 'recovery-done') {
      return { icon: '🎉', title: 'Password Updated!', body: 'Your password has been changed successfully. You can now log in with your new password.' };
    }
    if (emailType === 'email_change') {
      return { icon: '📧', title: 'Email Updated!', body: 'Your email address has been confirmed and updated successfully.' };
    }
    return { icon: '🎉', title: 'Email Confirmed!', body: 'Your email address has been verified. An admin will activate your account shortly — you can log in once activated.' };
  };

  return (
    <div className="auth-confirm-page">
      <div className="auth-confirm-card">
        <div className="app-logo">
          <div className="logo-star">⭐</div>
          <h1 className="app-name">{config.APP_NAME}</h1>
        </div>

        {status === 'loading' && (
          <div className="confirm-state">
            <div className="spinner" />
            <h2>Verifying your link…</h2>
            <p>Just a moment!</p>
          </div>
        )}

        {status === 'success' && (() => {
          const { icon, title, body } = successMessage();
          return (
            <div className="confirm-state success">
              <div className="big-icon">{icon}</div>
              <h2>{title}</h2>
              <p>{body}</p>
              <Link to="/login" className="confirm-btn">Go to Login</Link>
            </div>
          );
        })()}

        {status === 'reset-form' && (
          <div className="confirm-state">
            <div className="big-icon">🔑</div>
            <h2>Set New Password</h2>
            <p>Choose a strong password for your account.</p>
            <form onSubmit={handlePasswordReset} className="reset-form">
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="reset-input"
                required
                minLength={8}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="reset-input"
                required
              />
              {resetError && <p className="reset-error">{resetError}</p>}
              <button type="submit" className="confirm-btn" disabled={resetLoading}>
                {resetLoading ? 'Saving…' : '✅ Save New Password'}
              </button>
            </form>
          </div>
        )}

        {status === 'error' && (
          <div className="confirm-state error">
            <div className="big-icon">😕</div>
            <h2>Link Not Valid</h2>
            <p>{message}</p>
            <Link to="/signup" className="confirm-btn">Sign Up Again</Link>
            <Link to="/login" className="confirm-link">Already have an account? Log in</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthConfirm;
